// Peças reutilizáveis para as telas de cadastro, para manter tudo no mesmo
// padrão: barra com botão "Novo", tabela, e modal com formulário + ações.

import { emTitulo } from "../../lib/em-titulo.ts";
import { el, openModal, botao } from "../ui.ts";
import { icon } from "../icons.ts";

/**
 * O rótulo de um campo (`.campo-rotulo`) — com o asterisco quando o campo é
 * obrigatório. Um lugar só para `campo`, `campoObrigatorio` e `grupoCampos`.
 */
function rotuloCampo(tag: "span" | "legend", rotulo: string, obrigatorio: boolean): HTMLElement {
  return el(
    tag,
    { class: "campo-rotulo" },
    emTitulo(rotulo),
    obrigatorio ? el("span", { class: "campo-obrigatorio", "aria-hidden": "true" }, " *") : null,
  );
}

/** Uma linha rotulada de formulário (rótulo em cima, campo embaixo). */
export function campo(
  rotulo: string,
  campoEl: Node,
  hintOuExtra?: string | Node,
  ...extras: Node[]
): HTMLElement {
  const hint = typeof hintOuExtra === "string" ? hintOuExtra : undefined;
  const outros = typeof hintOuExtra === "string" ? extras : hintOuExtra ? [hintOuExtra, ...extras] : extras;
  return el(
    "label",
    { class: "campo" },
    rotuloCampo("span", rotulo, false),
    hint ? el("small", { class: "muted" }, hint) : null,
    campoEl,
    ...outros,
  );
}

/**
 * Igual `campo()`, mas marca o campo como obrigatório: asterisco ao lado do
 * rótulo (visual) + `aria-required` no campo (leitor de tela). Só use
 * quando o domínio de fato recusa o valor vazio — não é decoração, é a
 * mesma regra que já existe no comando (ex.: `criarCliente` rejeita nome
 * vazio).
 */
export function campoObrigatorio(
  rotulo: string,
  campoEl: HTMLElement,
  hintOuExtra?: string | Node,
  ...extras: Node[]
): HTMLElement {
  campoEl.setAttribute("aria-required", "true");
  const hint = typeof hintOuExtra === "string" ? hintOuExtra : undefined;
  const outros = typeof hintOuExtra === "string" ? extras : hintOuExtra ? [hintOuExtra, ...extras] : extras;
  return el(
    "label",
    { class: "campo" },
    rotuloCampo("span", rotulo, true),
    hint ? el("small", { class: "muted" }, hint) : null,
    campoEl,
    ...outros,
  );
}

/**
 * Um rótulo para **vários controles** (ex.: as linhas de peça + quantidade da
 * Compensação de Peças): `<fieldset>` + `<legend>` no mesmo visual de
 * `campo()`. Um `<label>` só associa um controle — por isso não serve aqui.
 * `obrigatorio` põe o asterisco e `aria-required` no grupo.
 */
export function grupoCampos(
  rotulo: string,
  opts: { obrigatorio?: boolean; hint?: string },
  ...conteudo: Node[]
): HTMLElement {
  const g = el(
    "fieldset",
    { class: "campo campo-grupo" },
    rotuloCampo("legend", rotulo, opts.obrigatorio === true),
    opts.hint ? el("small", { class: "muted" }, opts.hint) : null,
    ...conteudo,
  );
  if (opts.obrigatorio) g.setAttribute("aria-required", "true");
  return g;
}

/** `<input type="text">` já no padrão. */
export function inputTexto(
  placeholder: string,
  valor = "",
  extra: Record<string, string> = {},
): HTMLInputElement {
  const i = el("input", {
    type: "text",
    placeholder,
    ...extra,
  }) as HTMLInputElement;
  i.value = valor;
  return i;
}

/** `<input type="date">` já no padrão, para usar dentro de `campo()`. */
export function inputData(valor = "", extra: Record<string, string> = {}): HTMLInputElement {
  return marcaVazio(el("input", { type: "date", ...extra }) as HTMLInputElement, valor);
}

/** `<input type="time">` já no padrão ("HH:MM"). */
export function inputHora(valor = ""): HTMLInputElement {
  return marcaVazio(el("input", { type: "time" }) as HTMLInputElement, valor);
}

/**
 * `data-vazio` deixa o "dd/mm/aaaa" / "--:--" na cor de ausência de valor
 * (CSS, docs/design/ui.md "Cores"). O CSS não tem como saber se uma data ou
 * hora está vazia, e várias telas preenchem ou limpam o campo por código (sem
 * evento) — por isso o setter de `value` também atualiza a marca, não só o
 * que o usuário digita.
 */
function marcaVazio(i: HTMLInputElement, valor: string): HTMLInputElement {
  const marcar = (): void => {
    i.toggleAttribute("data-vazio", i.value === "");
  };
  const nativo = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")!;
  Object.defineProperty(i, "value", {
    configurable: true,
    get: () => nativo.get!.call(i) as string,
    set: (v: string) => {
      nativo.set!.call(i, v);
      marcar();
    },
  });
  i.addEventListener("input", marcar);
  i.addEventListener("change", marcar);
  i.value = valor;
  return i;
}

const OLHO_ABERTO =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></svg>';
const OLHO_FECHADO =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.9 17.9A10.1 10.1 0 0 1 12 20C5 20 1 12 1 12a18.5 18.5 0 0 1 5.1-6M9.9 4.2A9.1 9.1 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.2 3.2m-6.7-1.1a3 3 0 1 1-4.2-4.2"/><line x1="1" y1="1" x2="23" y2="23"/></svg>';

export interface CampoSenha {
  element: HTMLElement;
  readonly value: string;
  focus(): void;
  limpar(): void;
}

/** `<input type="password">` com um botão de olho que mostra/oculta o texto. */
export function inputSenha(
  placeholder = "Mínimo 6 caracteres",
  autocomplete: "new-password" | "current-password" = "new-password",
): CampoSenha {
  const input = el("input", {
    type: "password",
    autocomplete,
    placeholder,
  }) as HTMLInputElement;
  const olho = el("button", {
    type: "button",
    class: "senha-olho",
    "aria-label": "Mostrar Senha",
    tabindex: "-1",
  });
  olho.innerHTML = OLHO_ABERTO;
  olho.addEventListener("click", () => {
    const mostrar = input.type === "password";
    input.type = mostrar ? "text" : "password";
    olho.innerHTML = mostrar ? OLHO_FECHADO : OLHO_ABERTO;
    olho.setAttribute("aria-label", mostrar ? "Ocultar Senha" : "Mostrar Senha");
    input.focus();
  });
  return {
    element: el("div", { class: "campo-senha" }, input, olho),
    get value() {
      return input.value;
    },
    focus: () => input.focus(),
    limpar: () => {
      input.value = "";
    },
  };
}

/** `<select>` já no padrão, a partir de pares [valor, rótulo]. */
export function select(
  opcoes: readonly (readonly [string, string])[],
  atual: string,
): HTMLSelectElement {
  const s = el("select", {}) as HTMLSelectElement;
  for (const [v, r] of opcoes) {
    const o = el("option", { value: v }, r) as HTMLOptionElement;
    if (v === atual) o.selected = true;
    s.append(o);
  }
  return s;
}

export interface FormModalOpts {
  titulo: string;
  largura?: "padrao" | "largo";
  rotuloSalvar?: string;
  /** Monta os campos do formulário. */
  campos: () => Node;
  /**
   * Persiste. Deve lançar `Error` (mensagem exibida) se algo falhar.
   *
   * Repasse `aoErro` para o `store.executar` — assim a mensagem aparece só
   * aqui, dentro do formulário, em vez de aparecer também no toast.
   */
  salvar: (aoErro: (mensagem: string) => void) => Promise<void>;
}

/** Abre um modal com formulário padrão: campos + erro + Cancelar/Salvar. */
export function abrirFormModal(opts: FormModalOpts): void {
  openModal(
    opts.titulo,
    (fechar) => {
      const erro = el("p", { class: "form-erro", role: "alert" });
      const cancelar = botao("secundario", {}, "Cancelar");
      cancelar.addEventListener("click", fechar);
      const salvar = botao(
        "primario",
        { type: "submit" },
        opts.rotuloSalvar ?? "Salvar",
      );

      const form = el(
        "form",
        { class: "form-modal" },
        opts.campos(),
        erro,
        el("div", { class: "modal-actions" }, cancelar, salvar),
      );
      form.addEventListener("submit", (ev) => {
        ev.preventDefault();
        erro.textContent = "";
        salvar.disabled = true;
        opts
          .salvar((m) => {
            erro.textContent = m;
          })
          .then(fechar)
          .catch((e: unknown) => {
            // rede de segurança: erro que não passou pelo `aoErro`
            if (!erro.textContent) {
              erro.textContent =
                e instanceof Error && e.message
                  ? e.message
                  : "Não foi possível salvar.";
            }
          })
          .finally(() => {
            salvar.disabled = false;
          });
      });
      return form;
    },
    opts.largura ?? "padrao",
  );
}

export interface ConfirmacaoOpts {
  titulo: string;
  /**
   * A pergunta. Uma frase; o "não dá para desfazer" mora aqui. Aceita um
   * `Node` quando a mensagem precisa de ênfase (ex.: valores calculados em
   * negrito) — nesse caso é o chamador quem monta o conteúdo.
   */
  mensagem: string | Node;
  /** Rótulo do botão que confirma (padrão "Confirmar"). */
  rotuloConfirmar?: string;
  /** Ação destrutiva: pinta o botão de confirmar de vermelho. */
  perigo?: boolean;
  /** Ação ultra destrutiva: exige digitar exatamente esse texto ("ZERAR") pra habilitar. */
  exigirDigitacao?: string;
  aoConfirmar: () => void;
}

/**
 * Modal de confirmação padrão — no lugar do `confirm()` do navegador, que não
 * segue a marca nem o comportamento dos outros modais. "Cancelar" (secundário) é o
 * caminho seguro; o botão de confirmar fica à direita.
 */
export function abrirConfirmacao(opts: ConfirmacaoOpts): void {
  openModal(opts.titulo, (fechar) => {
    const cancelar = botao("secundario", {}, "Cancelar");
    cancelar.addEventListener("click", fechar);
    const confirmar = botao(
      opts.perigo ? "perigo-solido" : "primario",
      {},
      opts.rotuloConfirmar ?? "Confirmar",
    );
    confirmar.addEventListener("click", () => {
      fechar();
      opts.aoConfirmar();
    });

    if (opts.exigirDigitacao) {
      confirmar.disabled = true;
      const input = inputTexto(opts.exigirDigitacao);
      input.addEventListener("input", () => {
        confirmar.disabled = input.value !== opts.exigirDigitacao;
      });
      return el(
        "div",
        { class: "confirmacao confirmacao-digitacao" },
        el("p", {}, opts.mensagem),
        input,
        el("div", { class: "modal-actions" }, cancelar, confirmar),
      );
    }

    return el(
      "div",
      { class: "confirmacao" },
      el("p", {}, opts.mensagem),
      el("div", { class: "modal-actions" }, cancelar, confirmar),
    );
  });
}

/**
 * Une a barra de filtros, o campo de busca e a tabela (ou o aviso de vazio) num
 * só bloco visual. Passe os blocos na ordem em que aparecem. Um bloco `null`
 * (uma seção condicional, como as Perdas do Backlog) simplesmente não aparece —
 * mesma convenção de filho opcional que `el()` já usa.
 *
 * Os botões de ação da barra ("Nova OS", "Exportar"…) descem para a linha da
 * busca, à direita dela: a barra fica só com os filtros. Sem campo de busca
 * (a matriz do Backlog), eles ficam no fim da própria barra.
 */
export function telaLista(...blocos: (Node | null)[]): HTMLElement {
  // cada busca leva as ações da barra que vem logo antes dela (o Backlog tem
  // duas seções, cada uma com a sua barra)
  const ehBloco = (b: Node | null, classe: string): b is HTMLElement => b instanceof HTMLElement && b.classList.contains(classe);
  const presentes = blocos.filter((b): b is Node => b != null);
  const comAcoes = presentes.map((b, i) => {
    const anterior = presentes[i - 1] ?? null;
    if (!ehBloco(b, "busca-lista") || !ehBloco(anterior, "barra-filtros")) return b;
    const acoes = anterior.querySelector(":scope > .barra-filtros-fim");
    return acoes ? el("div", { class: "linha-busca-acoes" }, b, acoes) : b;
  });
  return el("div", { class: "tela-lista" }, ...comAcoes);
}

/** Normaliza para busca: sem acento, minúsculo, sem espaços nas pontas. */
export function normalizarBusca(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/** `termo` casa (como substring, sem acento/caixa) com algum dos `campos`. */
export function casaBusca(
  termo: string,
  ...campos: (string | null | undefined)[]
): boolean {
  const t = normalizarBusca(termo);
  if (t === "") return true;
  return campos.some((c) => c != null && normalizarBusca(c).includes(t));
}


/**
 * "Limpar Filtros" — só o ícone (funil com ×): ocupa menos espaço na barra; o
 * nome vai no aria-label e no title. Para a tela que monta a barra à mão.
 */
export function botaoLimparFiltros(aoLimpar: () => void): HTMLButtonElement {
  const limpar = botao(
    "secundario",
    { class: "btn-limpar btn-icone", "aria-label": "Limpar Filtros", title: "Limpar Filtros" },
    icon("limpar-filtro", 18),
  );
  limpar.addEventListener("click", aoLimpar);
  return limpar;
}

function montarBarra(
  filtros: readonly Node[],
  aoLimpar: (() => void) | undefined,
  extras: readonly Node[],
  primario: Node | null,
): HTMLElement {
  const filhos: Node[] = [...filtros];
  if (aoLimpar) filhos.push(botaoLimparFiltros(aoLimpar));
  if (extras.length > 0 || primario) {
    filhos.push(
      el("div", { class: "barra-filtros-fim" }, ...extras, primario),
    );
  }
  return el("div", { class: "barra-filtros" }, ...filhos);
}

/**
 * Barra do topo de uma tela de cadastro: filtros + "Limpar filtros" (à
 * esquerda) + botões secundários opcionais (`extras`) + o botão "Novo" (à
 * direita, sempre por último). `aoLimpar` deve devolver os filtros e a busca
 * ao padrão.
 *
 * Numa tela que só consulta (Auditoria), use `barraFiltros` — sem botão
 * primário. São duas funções em vez de um rótulo opcional justamente para não
 * existir a barra com um botão sem texto.
 *
 * `podeGravar` (issue #71, padrão `true`): quando `false`, o botão "Novo" nem
 * aparece — o Perfil do usuário logado só tem leitura neste Módulo.
 */
export function barraCadastro(
  filtros: readonly Node[],
  rotuloNovo: string,
  aoNovo: () => void,
  aoLimpar?: () => void,
  extras?: readonly Node[],
  podeGravar = true,
): HTMLElement {
  if (!podeGravar) return barraFiltros(filtros, aoLimpar, extras);
  const novo = botao(
    "primario",
    {},
    icon("plus", 14, { peso: 2.3 }),
    el("span", {}, rotuloNovo),
  );
  novo.addEventListener("click", aoNovo);
  return montarBarra(filtros, aoLimpar, extras ?? [], novo);
}

/** A mesma barra, para telas de consulta: sem botão primário. */
export function barraFiltros(
  filtros: readonly Node[],
  aoLimpar?: () => void,
  extras?: readonly Node[],
): HTMLElement {
  return montarBarra(filtros, aoLimpar, extras ?? [], null);
}

/** Um filtro `<select>` rotulado para a barra. */
export function filtro(
  rotulo: string,
  opcoes: readonly (readonly [string, string])[],
  atual: string,
  aoMudar: (v: string) => void,
): HTMLElement {
  const s = select(opcoes, atual);
  s.addEventListener("change", () => aoMudar(s.value));
  return el("label", { class: "filtro" }, emTitulo(rotulo), s);
}

/**
 * Um filtro rotulado com um controle qualquer (data, número…). Mesma moldura
 * do `filtro` de `<select>` — é por aqui que entra qualquer filtro que não
 * seja uma lista de opções.
 */
export function filtroCampo(rotulo: string, controle: Node): HTMLElement {
  return el("label", { class: "filtro" }, emTitulo(rotulo), controle);
}

/**
 * Campo rotulado para uma linha horizontal de inclusão (`.lista-add`). Mesmo
 * desenho do filtro: rótulo pequeno em cima, controle embaixo. Use sempre que
 * o controle não puder se explicar sozinho — uma data, por exemplo, não tem
 * `placeholder`, então sem rótulo vira uma caixa sem nome.
 */
export function campoInline(rotulo: string, controle: Node): HTMLElement {
  return el("label", { class: "campo-inline" }, emTitulo(rotulo), controle);
}

/**
 * Filtro de data. Aplica no `change`, como todos os outros filtros — nenhuma
 * tela tem botão "buscar" só para as datas.
 */
export function filtroData(
  rotulo: string,
  valor: string,
  aoMudar: (v: string) => void,
): HTMLElement {
  const i = inputData(valor);
  i.addEventListener("change", () => aoMudar(i.value));
  return filtroCampo(rotulo, i);
}
